import React from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import DonutCard from './charts/DonutCard';
import RateCard from './charts/RateCard';
import TopContentList from './charts/TopContentList';
import BigPercentCard from './charts/BigPercentCard';
import ActivityBarChart from './charts/ActivityBarChart';
import { getPlaneCharts } from '../../utils/planeChartConfig';

const CARD_COMPONENTS = {
  donut: DonutCard,
  rate: RateCard,
  list: TopContentList,
  bigPercent: BigPercentCard,
  bar: ActivityBarChart
};

const SPAN_CLASS = {
  1: 'lg:col-span-1',
  2: 'lg:col-span-2',
  3: 'lg:col-span-3',
  4: 'lg:col-span-4'
};

const container = { hidden: {}, show: { transition: { staggerChildren: 0.07 } } };
const item = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: 'easeOut' } }
};

/**
 * Chart band for a feature plane, rendered from utils/planeChartConfig.js.
 *
 * The config decides which cards appear and how they're fed; this component
 * only handles layout and animation. To change what a plane shows, edit the
 * config — nothing here needs to change.
 */
const PlaneAnalyticsBand = ({ plane, features, stats, accent, loading }) => {
  const cards = getPlaneCharts(plane);
  if (!cards.length) return null;

  return (
    <motion.div
      variants={container}
      initial="hidden"
      animate="show"
      className="grid grid-cols-1 lg:grid-cols-4 gap-3 mt-3"
    >
      {cards.map((card, index) => {
        const Component = CARD_COMPONENTS[card.type];
        if (!Component) return null;

        const props = card.build?.(features, stats, accent);
        if (!props) return null;

        return (
          <motion.div
            key={`${plane}-${card.type}-${index}`}
            variants={item}
            className={SPAN_CLASS[card.span] || SPAN_CLASS[1]}
          >
            <Component {...props} loading={loading} />
          </motion.div>
        );
      })}
    </motion.div>
  );
};

PlaneAnalyticsBand.propTypes = {
  plane: PropTypes.string.isRequired,
  features: PropTypes.array.isRequired,
  stats: PropTypes.object,
  accent: PropTypes.string.isRequired,
  loading: PropTypes.bool
};

export default PlaneAnalyticsBand;
